import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import {
  adminGa4Realtime,
  adminGa4Summary,
  adminGa4Pages,
  adminGa4Sources,
} from "@/lib/api";
import LiveUserMap from "./LiveUserMap";

const RANGES = [
  { key: "today",     label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "7days",     label: "Last 7 days" },
  { key: "28days",    label: "Last 28 days" },
  { key: "90days",    label: "Last 90 days" },
];

const fmtNum = (n) => (n || 0).toLocaleString();
const fmtPct = (n) => `${((n || 0) * 100).toFixed(1)}%`;
const fmtDur = (s) => {
  const t = Math.round(s || 0);
  const m = Math.floor(t / 60);
  const r = t % 60;
  return m > 0 ? `${m}m ${r}s` : `${r}s`;
};

const KpiCard = ({ label, value, hint, testid }) => (
  <div
    className="hairline p-5 bg-ground"
    data-testid={testid}
  >
    <div className="nav-item" style={{ color: "var(--stone)" }}>{label}</div>
    <div className="serif text-4xl mt-2" style={{ color: "var(--ink)" }}>{value}</div>
    {hint && (
      <div className="text-xs mt-1" style={{ color: "var(--stone)" }}>{hint}</div>
    )}
  </div>
);

const SectionHeader = ({ eyebrow, title, right }) => (
  <div className="flex items-end justify-between mb-4">
    <div>
      <div className="nav-item" style={{ color: "var(--stone)" }}>{eyebrow}</div>
      <div className="serif text-2xl mt-1">{title}</div>
    </div>
    {right}
  </div>
);

const ErrorBanner = ({ msg, testid }) =>
  msg ? (
    <div
      className="hairline px-4 py-3 mb-4 text-sm"
      style={{ borderColor: "var(--accent)", color: "var(--accent)" }}
      data-testid={testid || "analytics-error"}
    >
      {msg}
    </div>
  ) : null;

const AdminAnalytics = () => {
  const [range, setRange] = useState("28days");
  const [realtime, setRealtime] = useState(null);
  const [summary, setSummary] = useState(null);
  const [pages, setPages] = useState(null);
  const [sources, setSources] = useState(null);
  const [loadingKey, setLoadingKey] = useState(0); // triggers reload
  const [rtLoading, setRtLoading] = useState(false);
  const [reportsLoading, setReportsLoading] = useState(false);

  const loadRealtime = useCallback(async () => {
    setRtLoading(true);
    try {
      const r = await adminGa4Realtime();
      setRealtime(r);
    } catch (err) {
      setRealtime({ ok: false, error: err?.response?.data?.detail || err.message });
    } finally {
      setRtLoading(false);
    }
  }, []);

  const loadReports = useCallback(async (r) => {
    setReportsLoading(true);
    try {
      const [s, p, src] = await Promise.all([
        adminGa4Summary(r),
        adminGa4Pages(r),
        adminGa4Sources(r),
      ]);
      setSummary(s);
      setPages(p);
      setSources(src);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message;
      setSummary({ ok: false, error: msg });
      setPages({ ok: false, error: msg });
      setSources({ ok: false, error: msg });
    } finally {
      setReportsLoading(false);
    }
  }, []);

  // Initial + range change loads
  useEffect(() => {
    loadRealtime();
    loadReports(range);
  }, [range, loadingKey, loadRealtime, loadReports]);

  // Realtime auto-refresh every 30s
  useEffect(() => {
    const id = setInterval(() => loadRealtime(), 30000);
    return () => clearInterval(id);
  }, [loadRealtime]);

  const rangeLabel = useMemo(
    () => RANGES.find((x) => x.key === range)?.label || "Last 28 days",
    [range]
  );

  const totals = summary?.totals || {};
  const chartData = useMemo(
    () =>
      (summary?.series || []).map((d) => ({
        date: d.date?.slice(5) || "",
        sessions: d.sessions,
        users: d.users,
        views: d.views,
      })),
    [summary]
  );

  const anyError =
    (realtime && !realtime.ok && realtime.error) ||
    (summary && !summary.ok && summary.error) ||
    null;

  return (
    <div data-testid="admin-analytics-panel" className="space-y-10">
      {/* Header + range selector */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="nav-item" style={{ color: "var(--stone)" }}>MELAVI · Traffic</div>
          <div className="serif text-3xl mt-1">Analytics</div>
          <div className="text-xs mt-1" style={{ color: "var(--stone)" }}>
            Powered by Google Analytics 4 · cached for 5 min · realtime every 30s
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="hairline flex items-stretch" data-testid="analytics-range-group">
            <span
              className="nav-item px-3 py-1.5 border-r flex items-center"
              style={{ borderRightColor: "var(--ink)", color: "var(--stone)" }}
            >
              Range
            </span>
            <select
              value={range}
              onChange={(e) => setRange(e.target.value)}
              data-testid="analytics-range-select"
              className="nav-item px-3 py-1.5 bg-transparent focus:outline-none cursor-pointer"
              style={{ color: "var(--ink)" }}
            >
              {RANGES.map((r) => (
                <option key={r.key} value={r.key} data-testid={`analytics-range-${r.key}`}>
                  {r.label}
                </option>
              ))}
            </select>
          </label>
          <button
            onClick={() => setLoadingKey((k) => k + 1)}
            className="nav-item px-3 py-1.5 hairline hover:bg-ink hover:text-ground transition-colors"
            data-testid="analytics-refresh"
            disabled={reportsLoading}
          >
            {reportsLoading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      <ErrorBanner msg={anyError} />

      {/* Realtime — cinematic Live User Map */}
      <section data-testid="analytics-realtime">
        <div className="flex items-end justify-between mb-4">
          <div>
            <div className="nav-item" style={{ color: "var(--stone)" }}>Right now · last 30 minutes</div>
            <div className="serif text-2xl mt-1">Realtime activity</div>
          </div>
          <span className="nav-item" style={{ color: "var(--stone)" }}>
            {rtLoading ? "Updating…" : "Auto · 30s"}
          </span>
        </div>
        <LiveUserMap realtime={realtime} loading={rtLoading} />
      </section>

      {/* KPI cards */}
      <section data-testid="analytics-kpis">
        <SectionHeader eyebrow={rangeLabel} title="Overview" />
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <KpiCard label="Users" value={fmtNum(totals.users)} testid="kpi-users" />
          <KpiCard label="Sessions" value={fmtNum(totals.sessions)} testid="kpi-sessions" />
          <KpiCard label="Page views" value={fmtNum(totals.views)} testid="kpi-views" />
          <KpiCard
            label="Avg session"
            value={fmtDur(totals.avg_duration)}
            testid="kpi-avg-duration"
          />
          <KpiCard
            label="Bounce rate"
            value={fmtPct(totals.bounce_rate)}
            testid="kpi-bounce"
          />
        </div>
      </section>

      {/* Sessions chart */}
      <section data-testid="analytics-chart">
        <SectionHeader eyebrow="Trend" title="Daily sessions" />
        <div className="hairline p-4 bg-ground">
          {chartData.length === 0 ? (
            <div className="py-24 text-center nav-item" style={{ color: "var(--stone)" }}>
              {reportsLoading ? "Loading traffic…" : "No data for this window."}
            </div>
          ) : (
            <div style={{ width: "100%", height: 300 }}>
              <ResponsiveContainer>
                <AreaChart data={chartData} margin={{ top: 12, right: 12, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="melaviSessions" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#AE2B31" stopOpacity={0.28} />
                      <stop offset="100%" stopColor="#AE2B31" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="0" stroke="rgba(28,26,25,0.06)" vertical={false} />
                  <XAxis
                    dataKey="date"
                    stroke="#C9B99F"
                    tickLine={false}
                    axisLine={{ stroke: "rgba(28,26,25,0.15)" }}
                    style={{ fontSize: 11, fontFamily: "inherit" }}
                  />
                  <YAxis
                    stroke="#C9B99F"
                    tickLine={false}
                    axisLine={false}
                    width={40}
                    style={{ fontSize: 11 }}
                  />
                  <Tooltip
                    contentStyle={{
                      background: "#1C1A19",
                      border: "none",
                      color: "#F6F1E9",
                      fontSize: 12,
                      borderRadius: 0,
                    }}
                    labelStyle={{ color: "#C9B99F", letterSpacing: "0.18em", fontSize: 10 }}
                    cursor={{ stroke: "rgba(28,26,25,0.25)", strokeDasharray: "3 3" }}
                  />
                  <Area
                    type="monotone"
                    dataKey="sessions"
                    stroke="#AE2B31"
                    strokeWidth={2}
                    fill="url(#melaviSessions)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </section>

      {/* Top Pages + Top Sources */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <section data-testid="analytics-pages">
          <SectionHeader eyebrow="Where they read" title="Top pages" />
          <div className="hairline">
            <table className="w-full text-sm">
              <thead>
                <tr className="hairline-b" style={{ background: "rgba(28,26,25,0.04)" }}>
                  <th className="text-left px-4 py-3 nav-item" style={{ color: "var(--stone)" }}>
                    Page
                  </th>
                  <th className="text-right px-4 py-3 nav-item" style={{ color: "var(--stone)" }}>
                    Views
                  </th>
                </tr>
              </thead>
              <tbody>
                {(pages?.rows || []).map((r, i) => (
                  <tr key={i} className="hairline-b" data-testid={`page-row-${i}`}>
                    <td className="px-4 py-3">
                      <div className="serif">{r.title || "—"}</div>
                      <div className="text-xs" style={{ color: "var(--stone)" }}>{r.path}</div>
                    </td>
                    <td className="px-4 py-3 text-right serif">{fmtNum(r.views)}</td>
                  </tr>
                ))}
                {(!pages?.rows || pages.rows.length === 0) && (
                  <tr>
                    <td colSpan={2} className="py-10 text-center nav-item" style={{ color: "var(--stone)" }}>
                      {reportsLoading ? "Loading…" : "No page data."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section data-testid="analytics-sources">
          <SectionHeader eyebrow="Where they come from" title="Top sources" />
          <div className="hairline">
            <table className="w-full text-sm">
              <thead>
                <tr className="hairline-b" style={{ background: "rgba(28,26,25,0.04)" }}>
                  <th className="text-left px-4 py-3 nav-item" style={{ color: "var(--stone)" }}>
                    Source · medium
                  </th>
                  <th className="text-left px-4 py-3 nav-item" style={{ color: "var(--stone)" }}>
                    Campaign
                  </th>
                  <th className="text-right px-4 py-3 nav-item" style={{ color: "var(--stone)" }}>
                    Sessions
                  </th>
                </tr>
              </thead>
              <tbody>
                {(sources?.rows || []).map((r, i) => (
                  <tr key={i} className="hairline-b" data-testid={`source-row-${i}`}>
                    <td className="px-4 py-3">
                      <div className="serif">{r.source || "(direct)"}</div>
                      <div className="text-xs" style={{ color: "var(--stone)" }}>{r.medium || "—"}</div>
                    </td>
                    <td className="px-4 py-3 text-xs" style={{ color: "var(--stone)" }}>
                      {r.campaign && r.campaign !== "(not set)" ? r.campaign : "—"}
                    </td>
                    <td className="px-4 py-3 text-right serif">{fmtNum(r.sessions)}</td>
                  </tr>
                ))}
                {(!sources?.rows || sources.rows.length === 0) && (
                  <tr>
                    <td colSpan={3} className="py-10 text-center nav-item" style={{ color: "var(--stone)" }}>
                      {reportsLoading ? "Loading…" : "No source data."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AdminAnalytics;
